import useUrlParams from '../../../../hooks/useUrlParams'
import { useEffect, useMemo, useState, useRef } from 'react'
import { DownLinkSvg, ScheduleSvg, UplinkSvg } from '../Svgs'
import UpcommingContactsModal from './UpcommingContactsModal';
import CommandCenterService from '../../../../../../service/CommandCenterService';
import ValidateService from '../../../../../../service/ValidateService';
import { CircularProgress } from '@mui/material';
import useContacts from '../../../../hooks/useContacts';
import { v4 as uuidv4 } from "uuid";
import { useSelector } from 'react-redux';
import { MissionStore } from '../../../../../../store/MissionSlice';
import { get_formated_time } from '../../../../utils';
import { get_remaining_time } from '../../planning/utils';
import {
    containsSpecialChars,
    getCharacterLimitMessage,
    getSpecialCharacterMessage,
    isEmptyOrValidNumber,
    showToastMessage
} from '../../../../../../utils/commonFunctions';
import { validateBase64, validateNumberField } from './helpers/formValidation';
import { ContactStation } from '../../../../types';
import useTrueTwin from '../../../../hooks/useTrueTwin';
import CustomToolTip from '../../../../../CommonComponents/CustomToolTip';
import { SESSION_KEY_FAILURE_HANDLING, SESSION_KEY_EXECUTE_ONBOARD, CUSTOM_EVENT_STORAGE_UPDATE, CUSTOM_EVENT_REFRESH_ONBOARD_SCHEDULE } from '../../../../../../utils/constants';
import useFiles from '../../../../hooks/useFiles';
import useConsole from '../../../../hooks/useConsole';
import useScenario from '../../../../hooks/useScenario';
import { isOperateMode } from '../../../../hooks/utils';
import EncriptionSelectionSection from './EncriptionSelectionSection';
import useSecurityConfig from '../../../../hooks/useSecurityConfig';
import { SECRET_KEY_TASK_TYPES } from '../../../../../CommonComponents/security-configuration/helperFunctions';
import { SCENARIO_DEPLOYMENT_TYPES } from '../../../../../simulate/constants';

import { formatGsLabel, formatGsSubInfo } from './helpers/consoleContactHelpers';

/** Props for the execute/schedule button section at bottom of console left panel */
interface ExecuteButtonProps {
    postData: (payload: any) => Promise<void>;
    communicationType: string;
    isFormValid: any;
    formData: any;
    setFormContainerHeight: any;
    isValidationRunning?: boolean;
}

/** Handles command execution, contact selection, file upload, and recurring TC logic */
const FormSubmit: React.FC<ExecuteButtonProps> = ({
    postData,
    communicationType,
    isFormValid,
    formData,
    setFormContainerHeight,
    isValidationRunning = false,
}) => {
    const { dashboard, scenario_id, mission_id, sat_id } = useUrlParams();
    const { scenario } = useScenario();
    const operateMode = isOperateMode(dashboard, scenario);
    const { allACPStorageFiles, getAllMinioStorageFiles } = useFiles();
    const { addCommandUidToHighlight } = useConsole()
    const { truetwin_id } = useTrueTwin()
    const { securityConfigFormData, setSecurityConfigFormData, validateSecurityConfigFormData, handleUpdateSecurityConfigFormData } = useSecurityConfig()
    const { contacts, send_operate_task_command, send_operate_tc_command, send_operate_set_obc_task_command } = useContacts();
    const userData = useSelector((state: { mission: MissionStore }) => state.mission.userData);
    const { ongoing_contacts, upcoming_contacts } = contacts;

    const [openModal, setOpenModal] = useState<boolean>(false);
    const [isExecuteEnabled, setIsExecuteEnabled] = useState<boolean>(false);
    const [isValidJsonThere, setIsValidJsonThere] = useState<boolean>(true);
    const [isUploading, setIsUploading] = useState(false);
    const [delayDuration, setDelayDuration] = useState<string>('5');
    const [isRecurring, setIsRecurring] = useState(false);
    const formContainerRef = useRef(null);
    const [lastUploadedFileId, setLastUploadedFileId] = useState<any | null>(() => {
        const stored = localStorage.getItem("lastUploadedFileId");
        return stored ? JSON.parse(stored) : null;
    });

    const [upcommingContacts, setUpcommingContacts] = useState<{
        loading: boolean;
        contacts: any[];
        selected: ContactStation | null;
    }>({ loading: false, contacts: [], selected: null });

    const [selectedContact, setSelectedContact] = useState<{
        selection_type: 'manual' | 'auto';
        contact_type: string;
        contact: ContactStation;
    } | null>(null);

    /** True when deploymentType is VALIDATE — gates execute logic for TEST mode */
    const isValidateMode = useMemo(
        () => scenario?.scenarioDeploymentType?.toUpperCase() === SCENARIO_DEPLOYMENT_TYPES.VALIDATE,
        [scenario?.scenarioDeploymentType]
    );

    // ── Fetch upcoming GS contacts for schedule modal ─────────────────────────
    const get_upcomming_gs_contact_details = () => {
        setUpcommingContacts({ ...upcommingContacts, loading: true, contacts: [] });
        CommandCenterService.getUpcommingGroundContacts(scenario_id, sat_id, 'all')
            .then((res) => {
                if (res?.['data']) {
                    setOpenModal(true);
                    setUpcommingContacts({ ...upcommingContacts, loading: false, contacts: res['data'] });
                } else {
                    setUpcommingContacts({ ...upcommingContacts, loading: false, contacts: [] });
                }
            })
            .catch(() => {
                showToastMessage('error', 'Failed to get upcoming contact details');
                setUpcommingContacts({ ...upcommingContacts, loading: false, contacts: [] });
            });
    };

    // ── Form validity check (TT&C / TASK forms, via sessionStorage) ───────────
    const checkSelections = () => {
        const currentForm = sessionStorage.getItem('currentForm');
        const selectedBusComponent = sessionStorage.getItem('selectedBusComponent');
        const selectedTelecommand = sessionStorage.getItem('selectedTelecommand');
        const selectedPayload = sessionStorage.getItem('selectedPayload');
        const selectedSequences = sessionStorage.getItem('selectedSequences');
        const seqDuration = sessionStorage.getItem('seqDuration');
        const value = sessionStorage.getItem('value');
        const busComponent = selectedBusComponent ? JSON.parse(selectedBusComponent) : null;
        const telecommand = selectedTelecommand ? JSON.parse(selectedTelecommand) : null;
        const payload = selectedPayload ? JSON.parse(selectedPayload) : null;
        const sequences = selectedSequences ? JSON.parse(selectedSequences) : null;
        const duration = seqDuration !== null ? seqDuration : '';
        const rawBytes = sessionStorage.getItem('rawBytes');

        if (currentForm === 'bus') {
            setIsExecuteEnabled(busComponent !== null && telecommand !== null && isValidJsonThere);
        } else if (currentForm === 'customTc') {
            setIsExecuteEnabled(isValidJsonThere && telecommand !== null);
        } else if (currentForm === 'payload') {
            if (sequences?.label === 'Other Sequence') {
                setIsExecuteEnabled(payload !== null && sequences !== null && duration !== '' && value !== null && value !== '');
            } else {
                setIsExecuteEnabled(payload !== null && sequences !== null && duration !== '');
            }
        } else if (currentForm === 'raw') {
            setIsExecuteEnabled(validateBase64(rawBytes, showToastMessage));
        }
    };

    // ── Resize observer for dynamic layout ───────────────────────────────────
    useEffect(() => {
        const observer = new ResizeObserver(([entry]) =>
            setFormContainerHeight(entry.contentRect.height)
        );
        if (formContainerRef.current) observer.observe(formContainerRef.current);
        return () => observer.disconnect();
    }, [setFormContainerHeight]);

    const [executeOnboard, setExecuteOnboard] = useState<boolean>(() => {
        return sessionStorage.getItem(SESSION_KEY_EXECUTE_ONBOARD) === 'true';
    });
    useEffect(() => {
        checkSelections();
        const handleStorageUpdate = () => {
            checkSelections();
            setExecuteOnboard(sessionStorage.getItem(SESSION_KEY_EXECUTE_ONBOARD) === 'true');
        };
        window.addEventListener(CUSTOM_EVENT_STORAGE_UPDATE, handleStorageUpdate);
        return () => window.removeEventListener(CUSTOM_EVENT_STORAGE_UPDATE, handleStorageUpdate);
    }, []);

    useEffect(() => {
        const handleValidJSONEvent = (e) => {
            setIsValidJsonThere(e.detail.buttonOn);
            checkSelections();
            if (!e.detail.buttonOn) setIsExecuteEnabled(false);
        };
        window.addEventListener('validJSONEvent', handleValidJSONEvent);
        return () => window.removeEventListener('validJSONEvent', handleValidJSONEvent);
    }, []);

    useEffect(() => { checkSelections(); }, [isValidJsonThere]);

    useEffect(() => {
        const close_modal = () => setOpenModal(false);
        document.addEventListener('click', close_modal);
        return () => document.removeEventListener('click', close_modal);
    }, []);

    // ── Upload file status monitoring via WebSocket ───────────────────────────
    useEffect(() => {
        if (!allACPStorageFiles || !lastUploadedFileId) return;
        const fileId = lastUploadedFileId.uniqueId;
        let file: any = null;
        for (const folder of Object.values(allACPStorageFiles)) {
            if ((folder as Map<string, any>).has(fileId)) {
                file = (folder as Map<string, any>).get(fileId);
                break;
            }
        }
        if (!file) return;
        const storedFile = JSON.parse(localStorage.getItem("lastUploadedFileId") || "{}");
        if (storedFile?.uniqueId === file.uniqueId && storedFile?.fileStatus === file.fileStatus) return;
        if (file.fileStatus === "Completed") {
            showToastMessage("success", `${file.fileName} upload completed`);
            updateLastUploadedFileId(null);
        } else if (file.fileStatus === "Error") {
            showToastMessage("error", `${file.fileName} upload failed`);
            updateLastUploadedFileId(null);
        }
        localStorage.setItem("lastUploadedFileId", JSON.stringify(file));
    }, [allACPStorageFiles, lastUploadedFileId]);

    const updateLastUploadedFileId = (data: any | null) => {
        setLastUploadedFileId(data);
        if (data) localStorage.setItem("lastUploadedFileId", JSON.stringify(data));
        else localStorage.removeItem("lastUploadedFileId");
    };

    // ── Contact selection ─────────────────────────────────────────────────────
    const handleContactSelection = (contact, status) => {
        setSelectedContact({ selection_type: 'manual', contact_type: status, contact });
    };

    const handleContactDeselection = (event) => {
        event.stopPropagation();
        setUpcommingContacts({ ...upcommingContacts, selected: null });
        setSelectedContact(null);
    };

    const handleScheduleModalOpen = (event) => {
        event.stopPropagation();
        setOpenModal(!openModal);
    };

    // ── TASK disable logic ────────────────────────────────────────────────────
    const handle_execute_disable_for_task = useMemo(() => {
        const task_type = sessionStorage.getItem('taskType');
        const selected_task = sessionStorage.getItem('selected_task') ? JSON.parse(sessionStorage.getItem('selected_task') || '{}') : null;
        const selected_schedule = sessionStorage.getItem('selected_schedule') ? JSON.parse(sessionStorage.getItem('selected_schedule') || '{}') : null;
        if (!selectedContact) return true;
        if (task_type === 'adhoc' && !selected_task) return true;
        if (task_type === 'schedule' && !selected_schedule) return true;
        if (task_type === 'file' && operateMode && (!isFormValid || selectedContact?.['contact_type'] !== 'Ongoing')) return true;
        if (task_type === 'file' && !operateMode && (!isFormValid)) return true;
        if (task_type === '' || !task_type) return true;
        if (SECRET_KEY_TASK_TYPES.includes(task_type)) {
            return validateSecurityConfigFormData();
        }
        return false;
    }, [securityConfigFormData, selectedContact, isFormValid, sessionStorage.getItem('selected_task'), sessionStorage.getItem('selected_schedule'), sessionStorage.getItem('taskType')]); // add more dependencies if needed

    // ── TEST / TEST PLAN mode disable logic ──────────────────────────────────
    // Plain computation (not memoized) so it stays in sync with sessionStorage
    // updates emitted from the TEST / TEST PLAN panel.
    const isTestExecuteDisabled = (() => {
        if (communicationType === "TEST PLAN") {
            const testPlanId = sessionStorage.getItem("testPlanId");
            if (!testPlanId) return true;
        } else {
            const caseId = sessionStorage.getItem("testCaseId");
            if (!caseId) return true;
        }
        if (!isFormValid) return true;
        if (!selectedContact) return true;
        return false;
    })();

    // ── Main execute handler ──────────────────────────────────────────────────
    const handleExecute = async () => {
        const contact_id = selectedContact ? selectedContact.contact?.['uniqueId'] : '';
        const execute_now = !!(selectedContact && selectedContact.contact_type === 'Ongoing');

        // ── TEST / TEST PLAN execute ───────────────────────────────────────────
        if ((communicationType === 'TEST' || communicationType === 'TEST PLAN') && isValidateMode) {
            const testPlanId = communicationType === 'TEST PLAN'
                ? sessionStorage.getItem('testPlanId')
                : null;
            const testCaseId = communicationType === 'TEST'
                ? sessionStorage.getItem('testCaseId')
                : null;
            if (communicationType === 'TEST PLAN' && !testPlanId) { showToastMessage('error', 'Please select a test plan'); return; }
            if (communicationType === 'TEST' && !testCaseId) { showToastMessage('error', 'Please select a test case'); return; }
            if (!isFormValid) {
                showToastMessage('error', 'Please fill all required fields');
                return;
            }
            if (!selectedContact) {
                showToastMessage('error', 'Please select a ground station contact');
                return;
            }
            if (!mission_id || !sat_id || !scenario_id || !truetwin_id) {
                showToastMessage('error', 'Missing mission or satellite context');
                return;
            }

            try {
                const failureHandlingMode = sessionStorage.getItem(SESSION_KEY_FAILURE_HANDLING) || 'ABORT_ON_FAILURE';
                const isExecuteOnboard = sessionStorage.getItem(SESSION_KEY_EXECUTE_ONBOARD) === 'true';
                const executionLocation = isExecuteOnboard ? 'ONBOARD' : 'GROUND';

                if (communicationType === 'TEST PLAN') {
                    const res: any = await ValidateService.executeTestPlan(
                        mission_id,
                        testPlanId as string,
                        scenario_id,
                        {
                            truetwinId: truetwin_id,
                            satelliteId: sat_id,
                            contactId: contact_id,
                            executeNow: execute_now,
                            failureHandlingMode: failureHandlingMode,
                            executionLocation: executionLocation,
                        }
                    );
                    const successMsg = res?.data?.message || res?.message || 'Test plan execution initiated';
                    showToastMessage('success', successMsg);
                } else {
                    const res: any = await ValidateService.executeTestCase(
                        mission_id,
                        testCaseId as string,
                        scenario_id,
                        {
                            truetwinId: truetwin_id,
                            satelliteId: sat_id,
                            contactId: contact_id,
                            executeNow: execute_now,
                            failureHandlingMode: failureHandlingMode,
                            executionLocation: executionLocation,
                        }
                    );
                    const successMsg = res?.data?.message || res?.message || 'Test case execution initiated';
                    showToastMessage('success', successMsg);
                }
                window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
                window.dispatchEvent(new Event("validation_status_refetch"));
                window.dispatchEvent(new Event(CUSTOM_EVENT_REFRESH_ONBOARD_SCHEDULE));
            } catch (error: any) {
                const msg =
                    error?.response?.data?.message ??
                    error?.message ??
                    (communicationType === 'TEST PLAN'
                        ? 'Something went wrong while executing the test plan'
                        : 'Something went wrong while executing the test case');
                showToastMessage('error', msg);
            }
            return;
        }

        // ── TT&C / TASK / simulation execute (unchanged original logic) ────────
        if (operateMode) {
            if (communicationType === 'TT&C') {
                const encriptioEnabled = securityConfigFormData?.encriptionEnabled && securityConfigFormData?.encriptionKey?.value !== 'none'
                const encriptionKey = securityConfigFormData?.encriptionKey?.value !== 'none' ? securityConfigFormData?.encriptionKey?.id : 0
                const currentForm = sessionStorage.getItem('currentForm');
                const isCustomTc = currentForm === 'customTc';

                try {
                    const form = JSON.parse(sessionStorage.getItem('formData') || '{}');
                    // The bus form can briefly hold a payload without the command identity; the dropdown
                    // selection is the source of truth for which command was chosen, so fall back to it.
                    const formSequenceData = form?.[0]?.sequenceData;
                    const selectedCommand = currentForm === 'bus'
                        ? JSON.parse(sessionStorage.getItem('selectedTelecommand') || 'null')?.value
                        : null;
                    const lacksIdentity = formSequenceData?.commandId === undefined && !formSequenceData?.command;
                    const identityFallback = lacksIdentity && selectedCommand
                        ? { ...selectedCommand.sequenceData, command: selectedCommand.command }
                        : {};
                    const updated_form: any = {
                        CommandUid: uuidv4(),
                        CommandName: form?.[0]?.CommandName ?? (lacksIdentity ? selectedCommand?.name : undefined),
                        Type: form?.[0]?.Type,
                        sequenceData: {
                            ...identityFallback,
                            ...formSequenceData,
                            "IsEncryptionEnabled": encriptioEnabled,
                            "ExtendedHeaderIdx": encriptionKey,
                        },
                        IsRecurring: isRecurring,
                        Delay: isRecurring ? parseFloat(delayDuration || "0") : 0,
                        ...(isCustomTc || form?.[0]?.isCustomTc ? { IsCustomTc: true } : {}),
                    };
                    await send_operate_tc_command(contact_id, execute_now, updated_form);
                    window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
                    addCommandUidToHighlight(updated_form?.CommandUid);
                } catch (error: any) {
                    const msg =
                        error?.response?.data?.message ??
                        error?.message ??
                        'Something went wrong while executing the command';
                    showToastMessage('error', msg);
                }
                window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
            } else if (communicationType === 'TASK') {
                const task_type = sessionStorage.getItem('taskType');
                const selected_task = sessionStorage.getItem('selected_task') ? JSON.parse(sessionStorage.getItem('selected_task') || '{}') : null;
                const selected_schedule = sessionStorage.getItem('selected_schedule') ? JSON.parse(sessionStorage.getItem('selected_schedule') || '{}') : null;
                if (task_type === 'adhoc' && !selected_task) { showToastMessage('error', 'Please select a task'); return; }
                else if (task_type === 'schedule' && !selected_schedule) { showToastMessage('error', 'Please select a schedule'); return; }
                else if (task_type === 'adhoc' && selected_task) {
                    try { await send_operate_task_command(task_type, execute_now, { contactId: contact_id, taskId: selected_task.taskId }); }
                    catch (error: any) { showToastMessage('error', error?.response?.data?.message ?? 'Failed to execute task'); }
                } else if (task_type === 'schedule' && selected_schedule) {
                    try { await send_operate_task_command(task_type, execute_now, { contactId: contact_id, scheduleId: selected_schedule.scheduleId }); }
                    catch (error: any) { showToastMessage('error', error?.response?.data?.message ?? 'Failed to execute schedule'); }
                } else if (task_type === 'set_obc_curr_time') {
                    try { await send_operate_set_obc_task_command(task_type, execute_now, { contactId: contact_id }); }
                    catch (error: any) { showToastMessage('error', error?.response?.data?.message ?? 'Failed to set OBC time'); }
                } else if (task_type === 'file') {
                    handleSubmit()
                } else if (task_type && SECRET_KEY_TASK_TYPES.includes(task_type) && securityConfigFormData) {
                    setSecurityConfigFormData((prev => ({ ...prev!, loading: true })))
                    handleUpdateSecurityConfigFormData(contact_id, scenario_id!, truetwin_id!, execute_now, securityConfigFormData)
                }
            }
        } else if (dashboard === 'simulation' || dashboard === 'quick-deploy') {
            try {
                const form = JSON.parse(sessionStorage.getItem('formData') || '{}');
                form[0].LinkId = selectedContact ? selectedContact?.['uplink']?.['acpBandUid'] : '';
                form[0].ScheduleId = selectedContact ? selectedContact?.['groundScheduleId'] : form[0].ScheduleId;
                form[0].Timestamp = selectedContact
                    ? selectedContact.contact_type === 'Ongoing' ? 0 : (selectedContact?.['startTime'] * 1000) + 10000
                    : 0;
                await postData(form);
                setSelectedContact(null);
                setUpcommingContacts({ ...upcommingContacts, selected: null });
                window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
            } catch (error: any) {
                const msg =
                    error?.response?.data?.message ??
                    error?.message ??
                    'Something went wrong while executing the command';
                showToastMessage('error', msg);
                setSelectedContact(null);
            }
            window.dispatchEvent(new Event(CUSTOM_EVENT_STORAGE_UPDATE));
        }
    };

    // ── Auto-select first ongoing contact ─────────────────────────────────────
    useEffect(() => {
        if (!ongoing_contacts || ongoing_contacts.length === 0) {
            if (selectedContact?.contact_type === 'Ongoing') setSelectedContact(null);
            return;
        }
        const ongoingSorted = ongoing_contacts
            .filter((c) => c?.uplinkConfigured)
            .sort((a, b) => a.startTime - b.startTime);
        const firstOngoing = ongoingSorted[0];
        const isSelectedOngoing = ongoing_contacts.some((c) => c.uniqueId === selectedContact?.contact?.uniqueId);
        if (
            !selectedContact ||
            selectedContact?.['selection_type'] === 'auto' ||
            firstOngoing?.uniqueId === selectedContact?.contact?.uniqueId ||
            (selectedContact?.contact_type === 'Ongoing' && !isSelectedOngoing)
        ) {
            setSelectedContact({ selection_type: 'auto', contact_type: 'Ongoing', contact: firstOngoing });
        }
    }, [ongoing_contacts]);

    // NOTE:
    // Do NOT auto-select upcoming contacts in TEST.
    // TT&C requires an explicit user click for upcoming scheduling, and auto-selecting
    // here causes the "selected upcoming card" to reappear after clicking the cross,
    // creating a visible blink/flicker.

    // ── Derived display state ─────────────────────────────────────────────────
    const showGsListContainer = operateMode ? 'show_container' : '';

    const isExecuteOnboard =
        (communicationType === "TEST" || communicationType === "TEST PLAN") && executeOnboard;

    const isScheduleMode =
        ((communicationType === "TEST" || communicationType === "TEST PLAN") && selectedContact?.["contact_type"] === "Upcomming") ||
        (operateMode && (
            !ongoing_contacts ||
            (selectedContact && selectedContact?.['contact_type'] === 'Upcomming') ||
            (ongoing_contacts && !selectedContact && ongoing_contacts?.filter((c) => c?.['uplinkConfigured'])?.length === 0)
        ));
    const button_text = isExecuteOnboard
        ? "Upload & Execute"
        : (isScheduleMode ? "Schedule Now" : "Execute now");

    const show_multicontact = Boolean(ongoing_contacts?.filter((c) => c?.['uplinkConfigured'])?.length);

    if (userData?.["user_roles"]?.[0] === "project_viewer") return null;

    const timeUntilNextContact = get_remaining_time(
        Math.round(contacts?.current_time ?? 0),
        Math.round(upcoming_contacts?.[0]?.startTime ?? 0)
    );

    // ── File upload helpers (unchanged) ───────────────────────────────────────
    const handleSubmit = () => {
        if (!isFormValid) { showToastMessage('error', 'Please fill all required fields'); return; }
        const ftmFileIdValue = validateNumberField(formData.ftmFileId, 'File ID', showToastMessage);
        if (ftmFileIdValue === null) return;
        const destinationIdValue = validateNumberField(formData.destinationId, 'Destination', showToastMessage);
        if (destinationIdValue === null) return;
        uploadAPI(formData.fileObject, formData.fileName);
    };

    const uploadAPI = (file_data, fileName) => {
        setIsUploading(true);
        CommandCenterService
            .postUploadFileConsoleFlatSat(
                mission_id, truetwin_id, scenario_id,
                formData.fileId, formData.isLossDetection, formData.mtuValue,
                formData.delayValue, formData.activityTimeoutValue,
                formData.connectionTimeoutValue, formData.destinationId, formData.ftmFileId
            )
            .then((response) => handleUploadResponse(response, file_data))
            .catch(handleUploadError);
    };

    const handleUploadResponse = (response, fileData) => {
        if (response) {
            showToastMessage('success', response?.data?.message ?? 'File Uplink started');
            setIsUploading(false);
            updateLastUploadedFileId(fileData);
            getAllMinioStorageFiles(truetwin_id);
        } else {
            setIsUploading(false);
            showToastMessage('error', 'File uplink failed');
            updateLastUploadedFileId(null);
        }
    };

    const handleUploadError = (error) => {
        setIsUploading(false);
        showToastMessage('error', error?.response?.data?.message ?? 'Error uploading file. Please try again.');
        updateLastUploadedFileId(null);
    };

    const handleDelayDurationChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { value } = e.target;
        const numericValue = Number(value);
        if (isEmptyOrValidNumber(value) && !containsSpecialChars(value) && !isNaN(numericValue) && numericValue >= 0) {
            setDelayDuration(value.trim());
        } else {
            showToastMessage('error', value.length > 52 ? getCharacterLimitMessage(52) : getSpecialCharacterMessage());
        }
    };

    const handleDelayDurationBlur = (e: React.FocusEvent<HTMLInputElement>) => {
        const { value } = e.target;
        if (value !== "" && Number(value) < 5) {
            showToastMessage('error', 'Delay must be at least 5sec');
            setDelayDuration("5");
        }
    };

    const isValidationActiveForCurrentTab =
        isValidationRunning && (communicationType === 'TEST' || communicationType === 'TEST PLAN' || communicationType === 'TASK');

    // ── Compute execute button disabled state ─────────────────────────────────
    const isExecuteDisabled = (() => {
        if (isUploading || isValidationActiveForCurrentTab) return true;

        if (communicationType === 'TEST' || communicationType === 'TEST PLAN') {
            return isTestExecuteDisabled;
        }

        if (!operateMode) {
            return communicationType === 'TT&C' ? !isExecuteEnabled : !isFormValid;
        }

        // operateMode
        if (communicationType === 'TT&C') return !isExecuteEnabled || !selectedContact;
        return handle_execute_disable_for_task;
    })();

    return (
        <div ref={formContainerRef} className={`form__submit__section__container ${operateMode && communicationType === "TT&C" ? "form__submit__section__container__small_gap" : ""}`}>

            {/* Ground-station contact list
                Shown in operate mode AND in TEST mode (validate always needs a contact) */}
            <div className={`contact__gs__list__container ${showGsListContainer || communicationType === 'TEST' || communicationType === 'TEST PLAN' ? 'show_container' : ''}`}>
                {ongoing_contacts && ongoing_contacts?.length > 1 && (
                    <div className='multiple__contacts__info__card'>
                        <span className='multiple__contacts__info__text'>Multiple ground station in contacts</span>
                    </div>
                )}

                {/* Ongoing contact cards */}
                {ongoing_contacts?.map((contact: ContactStation) => (
                    <button
                        key={contact?.['uniqueId']}
                        disabled={!contact?.['uplinkConfigured']}
                        className={`gs__details__container ${contact?.['uniqueId'] === selectedContact?.contact?.['uniqueId'] ? 'selected' : ''}`}
                        onClick={() => handleContactSelection(contact, 'Ongoing')}
                    >
                        <div className="w-30p d-flex">
                            {contact?.['uplinkConfigured'] && <UplinkSvg />}
                            {contact?.['downlinkConfigured'] && <DownLinkSvg />}
                        </div>
                        <div className="d-flex flex-column gap-1 w-90">
                            <div className="d-flex w-100 align-items-center justify-content-between">
                                {formatGsLabel(contact) && (
                                    <span className="gs__details__label w-60">
                                        {formatGsLabel(contact)}
                                    </span>
                                )}
                                {formatGsSubInfo(get_formated_time(contact?.['startTime'])) && (
                                    <span className="gs__details__other__info w-40 text-end">
                                        {get_formated_time(contact?.['startTime'])}
                                    </span>
                                )}
                            </div>
                            <div className="d-flex w-100 align-items-center justify-content-between">
                                {formatGsSubInfo(contact?.['groundStationProviderName']) && (
                                    <span className="gs__details__other__info w-60 text-start">
                                        {formatGsSubInfo(contact?.['groundStationProviderName'])}
                                    </span>
                                )}
                                {formatGsSubInfo(contact?.['bandInfo']?.['bandName']) && (
                                    <span className="gs__details__other__info">
                                        {formatGsSubInfo(contact?.['bandInfo']?.['bandName'])}
                                    </span>
                                )}
                            </div>
                        </div>
                    </button>
                ))}

                {/* Selected upcoming contact card */}
                {selectedContact?.['contact_type'] === 'Upcomming' && (
                    <div className="gs__details__container selected">
                        <div className="w-30p d-flex">
                            {selectedContact?.['contact']?.['uplinkConfigured'] && <UplinkSvg />}
                            {selectedContact?.['contact']?.['downlinkConfigured'] && <DownLinkSvg />}
                        </div>
                        <div className="d-flex flex-column gap-1 w-90">
                            <div className="w-100 d-flex align-items-center justify-content-between">
                                {formatGsLabel(selectedContact?.['contact']) && (
                                    <span className="gs__details__label w-60">
                                        {formatGsLabel(selectedContact?.['contact'])}
                                    </span>
                                )}
                                {formatGsSubInfo(get_formated_time(selectedContact?.['contact']?.['startTime'])) && (
                                    <span className="gs__details__other__info w-40 text-end">
                                        {get_formated_time(selectedContact?.['contact']?.['startTime'])}
                                    </span>
                                )}
                            </div>
                            <div className="d-flex w-100 align-items-center justify-content-between">
                                {formatGsSubInfo(selectedContact?.['contact']?.['groundStationProviderName']) && (
                                    <span className="gs__details__other__info">
                                        {formatGsSubInfo(selectedContact?.['contact']?.['groundStationProviderName'])}
                                    </span>
                                )}
                                {formatGsSubInfo(selectedContact?.['contact']?.['bandInfo']?.['bandName']) && (
                                    <span className="gs__details__other__info">
                                        {formatGsSubInfo(selectedContact?.['contact']?.['bandInfo']?.['bandName'])}
                                    </span>
                                )}
                            </div>
                        </div>
                        <button className="cancel__contact" onClick={handleContactDeselection}>
                            <i className="fe fe-x" />
                        </button>
                    </div>
                )}

                {/* Next upcoming contact */}
                {upcoming_contacts?.[0] &&
                    upcoming_contacts[0]?.['uniqueId'] !== selectedContact?.contact?.['uniqueId'] && (
                        <button
                            disabled={!upcoming_contacts[0]?.['uplinkConfigured']}
                            className={`gs__details__container ${upcoming_contacts[0]?.['uniqueId'] === selectedContact?.contact?.['uniqueId'] ? 'selected' : ''}`}
                            onClick={() => handleContactSelection(upcoming_contacts[0], 'Upcomming')}
                        >
                            <div className="w-30p d-flex">
                                {upcoming_contacts[0]?.['uplinkConfigured'] && <UplinkSvg />}
                                {upcoming_contacts[0]?.['downlinkConfigured'] && <DownLinkSvg />}
                            </div>
                            <div className="d-flex flex-column gap-1 w-90">
                                <div className="d-flex w-100 align-items-center justify-content-between">
                                    {formatGsLabel(upcoming_contacts[0]) && (
                                        <span className="gs__details__label w-60">
                                            {formatGsLabel(upcoming_contacts[0])}
                                        </span>
                                    )}
                                    {formatGsSubInfo(timeUntilNextContact) && (
                                        <span className="gs__details__other__info w-40 text-end">
                                            {timeUntilNextContact}
                                        </span>
                                    )}
                                </div>
                                <div className="d-flex w-100 align-items-center justify-content-between">
                                    {formatGsSubInfo(upcoming_contacts[0]?.['groundStationProviderName']) && (
                                        <span className="gs__details__other__info w-60 text-start">
                                            {formatGsSubInfo(upcoming_contacts[0]?.['groundStationProviderName'])}
                                        </span>
                                    )}
                                    {formatGsSubInfo(upcoming_contacts[0]?.['bandInfo']?.['bandName']) && (
                                        <span className="gs__details__other__info">
                                            {formatGsSubInfo(upcoming_contacts[0]?.['bandInfo']?.['bandName'])}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </button>
                    )}
            </div>

            {/* --------Select ground station button--------  */}
            {/* {button_text === 'Schedule Now' && !selectedContact &&
                <button
                    className='select__ground__station__btn'
                    onClick={(event: any) => {
                        event.stopPropagation();
                        setOpenModal(!openModal); //open contacts modal
                    }}
                    disabled={upcommingContacts?.loading}
                >
                    <span className='select__ground__station__btn__text'>Select Ground Stations</span>
                </button>
            } */}
            {/* ----------------------------------- */}

            {/* -----------------Recurring Section------------------ */}
            {operateMode && communicationType === "TT&C" && (
                <div className="recurring-container">
                    {/* Recurring checkbox + label */}
                    <div className='__checkbox__container'>
                        <input className="__checkbox" type="checkbox" id="encription"
                            checked={isRecurring}
                            onChange={e => {
                                const checked = e.target.checked;
                                setIsRecurring(checked);
                                if (!checked) {
                                    setDelayDuration("5");
                                }
                            }}
                        />
                        <span className="__checkbox__label">Recurring</span>
                    </div>

                    {isRecurring && (
                        <div className="recurring-delay-row">
                            {/* Delay label */}
                            <div className="recurring-delay-label">Delay</div>

                            {/* Delay input with "secs" suffix */}
                            <div className="recurring-delay-input-group">
                                <input
                                    required
                                    type="number"
                                    value={delayDuration}
                                    onKeyDown={(e) => e.key === "Enter" && e.preventDefault()}
                                    onChange={handleDelayDurationChange}
                                    min="0"
                                    className="recurring-delay-input"
                                    onBlur={handleDelayDurationBlur}
                                />

                                <button
                                    type="button"
                                    onClick={(e) => e.preventDefault()}
                                    className="recurring-delay-suffix"
                                >
                                    sec
                                </button>
                            </div>
                        </div>
                    )}
                </div>
            )}
            {/* -----------------Recurring Section------------------ */}

            {/* ----------------Encription Selection------------------- */}
            {operateMode && (communicationType === 'TT&C' || (communicationType === 'TASK' && SECRET_KEY_TASK_TYPES.includes(sessionStorage.getItem('taskType') ?? ''))) && <EncriptionSelectionSection />}
            {/* ----------------Encription Selection------------------- */}

            <div className={`form__submit__btn__container`}>
                <button
                    className='execute__btn'
                    onClick={handleExecute}
                    disabled={isExecuteDisabled}
                    data-testid="console-execute-btn"
                >
                    {isUploading ? "File Uploading In progress..." : isValidationActiveForCurrentTab ? "Execution In Progress..." : button_text}
                    {(isUploading || isValidationActiveForCurrentTab) && <CircularProgress size={14} className='text-white ms-2' />}
                </button>

                {(() => {
                    const hasUpcoming = Array.isArray(upcoming_contacts) && upcoming_contacts.length > 0;
                    const isScheduleIconDisabled = upcommingContacts?.loading || !hasUpcoming;
                    return (
                        <div className={`schedule__btn__container ${operateMode || communicationType === 'TEST' || communicationType === 'TEST PLAN' ? 'show_btn small' : ''}`}>
                            <button
                                name='scheduleSvgButton'
                                disabled={isScheduleIconDisabled}
                                className="schedule__btn"
                                onClick={handleScheduleModalOpen}
                            >
                                {upcommingContacts.loading
                                    ? <CircularProgress size={15} />
                                    : (
                                        <CustomToolTip disabled={hasUpcoming} title='No data available' placement='top'>
                                            <ScheduleSvg />
                                        </CustomToolTip>
                                    )
                                }
                            </button>
                            {hasUpcoming && (
                                <UpcommingContactsModal
                                    open={openModal}
                                    setOpenModal={setOpenModal}
                                    upcommingContacts={upcoming_contacts}
                                    selectedContact={selectedContact?.contact || null}
                                    setSelectedContact={(contact) => {
                                        setSelectedContact({ selection_type: 'manual', contact_type: 'Upcomming', contact });
                                        setOpenModal(false);
                                    }}
                                />
                            )}
                        </div>
                    );
                })()}
            </div>
        </div>
    );
};

export default FormSubmit;
